// The valley's fight, drawn (phase B joined): every enemy as a figure on the bodies module (src/bodies/figures.ts:
// motion, action poses and hit reactions, the Warden's phase card, ragdolls that dissolve into motes), the hero's weapon
// on its hand socket following the combat state (its poses go on in src/valley/look.ts), and the impact director
// (src/feedback/) on the valley's camera rig: hit-stop by the hit's kind, flashes, shake and kick, particles,
// telegraphs draped on the valley's ground, weapon trails. A view: it reads the sim and never writes it.
import * as THREE from "three";
import type { Attachment, Figure, PoseSample } from "@rpg/figure";
import type { LowPolyLook } from "@rpg/look";
import type { CameraRig } from "@rpg/camera";
import type { EventBus } from "@rpg/kernel";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { GameEvents } from "../core";
import { mountFeedback, reduceMotionOf, type Feedback } from "../feedback";
import { mountFigureSet, sampleWeapon, type FigureSet } from "../bodies/figures";
import { HERO_ID, type Fight } from "../enemies/fight";
import type { ValleyRig } from "./scene";
import { CAMERA_LAYERS } from "./camera";

/** The fight's view in the valley. */
export interface ValleyFightView {
  feedback: Feedback;
  figures: FigureSet;
  /** The hero's held attachment id. */
  held(): string | null;
  /** Clear what the warm-up showed (call after the look's warm-up render). */
  clearWarm: () => void;
}

/** The hero's action poses and reactions at a frame's alpha (the bodies module's track), for the valley's look. */
export function heroPoses(fight: () => Fight, runtime: Runtime): (alpha: number) => PoseSample | null {
  return (alpha) => {
    const f = fight();
    const t = f.bodies.state().t + (alpha - 1) * runtime.clock.dt;
    return f.bodies.sample(HERO_ID, f.heroCard, t);
  };
}

/** Mount the fight's view; call it inside the valley look's warm-up (its `warm` hook). */
export function mountValleyFightView(o: {
  runtime: Runtime;
  scene: THREE.Scene;
  look: LowPolyLook;
  content: SpikeContent;
  fight: () => Fight;
  rig: () => ValleyRig;
  hero: { figure: Figure };
  events: EventBus<GameEvents>;
  camera: CameraRig | null;
  setting?: (id: string) => unknown;
}): ValleyFightView {
  const f = o.fight();
  const reg = o.content.registry;
  const attachments = new Map<string, Attachment>(reg.rows("attachments").map((a) => [a.id, a as unknown as Attachment]));
  const probe = (x: number, z: number, fromY: number): number | null => o.rig().collision.castRay({ x, y: fromY, z }, { x: 0, y: -1, z: 0 }, 40, { layers: CAMERA_LAYERS })?.point.y ?? null;

  // the hero's weapon on its socket, following the combat state's weapon
  let held: string | null = null;
  const hold = (): void => {
    const w = f.view(HERO_ID)?.weapon ?? null;
    const id = w ? (f.content.combat.attachmentOf.get(w) ?? null) : null;
    if (id === held || !o.hero.figure.card.socket) return;
    held = id;
    o.hero.figure.attach(id ? (attachments.get(id) ?? null) : null);
  };
  hold();

  let figures: FigureSet | null = null;
  const tips = new Map<string, THREE.Vector3>();
  const feedback = mountFeedback({
    runtime: o.runtime,
    events: o.events,
    scene: o.scene,
    content: o.content,
    rig: o.camera,
    weaponOf: (owner) => {
      if (owner !== HERO_ID) return figures?.weapon(owner) ?? null;
      return held ? sampleWeapon(o.hero.figure, held, tips) : null;
    },
    heightAt: (x, z) => probe(x, z, 60) ?? o.rig().terrainAt(x, z),
    motion: () => ({ shake: Number(o.setting?.("camera.shake") ?? 1), reduceMotion: reduceMotionOf(o.setting?.("accessibility.reduceMotion")) }),
  });
  feedback.track(HERO_ID, o.hero.figure.root);

  figures = mountFigureSet({
    runtime: o.runtime,
    scene: o.scene,
    look: o.look,
    content: o.content,
    bodies: () => o.fight().bodies,
    collision: () => o.rig().collision,
    entries: () => {
      const fight = o.fight();
      const out = [];
      for (const e of fight.state().enemies) {
        if (!e.alive) continue;
        const kind = fight.content.kinds[e.kind]!;
        const card = e.phase > 1 ? (kind.phases[e.phase - 2]?.card ?? kind.card) : kind.card;
        out.push({ id: e.id, card, pos: e.pos, yaw: e.facing, vel: e.vel, acting: fight.view(e.id)?.move != null });
      }
      return out;
    },
    probe,
    onAdd: (id, root) => feedback.track(id, root),
    onRemove: (id) => feedback.untrack(id),
    order: -14,
    name: "valley fight view",
  });

  o.runtime.on(
    "late",
    (c) => {
      if (c.mode === "play" && o.runtime.paused) return;
      hold();
    },
    { order: -14, name: "valley fight view: the hero's weapon" },
  );

  // compile the director's programs in the look's warm-up frame, in front of the hero
  const h = o.rig().hero();
  const clearWarm = feedback.warm([h.feet.x + Math.sin(h.yaw) * 2, h.feet.y, h.feet.z + Math.cos(h.yaw) * 2]);
  return { feedback, figures, held: () => held, clearWarm };
}
